import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";

interface WheelTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  timeSlots: string[];
  placeholder?: string;
  error?: boolean;
  className?: string;
}

export function WheelTimePicker({
  value,
  onChange,
  timeSlots,
  placeholder = "Seleccionar hora",
  error = false,
  className,
}: WheelTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemHeight = 48; // Height of each time slot item

  // Find current index
  const currentIndex = timeSlots.findIndex((slot) => slot === value);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Scroll to selected value when opening
  useEffect(() => {
    if (isOpen && scrollRef.current && currentIndex >= 0) {
      const scrollPosition = currentIndex * itemHeight;
      scrollRef.current.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [isOpen, currentIndex, itemHeight]);

  // Handle scroll snap selection
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    
    const scrollTop = scrollRef.current.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    
    if (index >= 0 && index < timeSlots.length) {
      const newValue = timeSlots[index];
      if (newValue !== value) {
        onChange(newValue);
      }
    }
  }, [itemHeight, onChange, timeSlots, value]);

  // Debounced scroll handler
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !isOpen) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    scrollElement.addEventListener("scroll", handleScrollEnd);
    
    return () => {
      scrollElement.removeEventListener("scroll", handleScrollEnd);
      clearTimeout(scrollTimeout);
    };
  }, [isOpen, handleScroll]);

  const handleItemClick = (slot: string, index: number) => {
    onChange(slot);
    
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: index * itemHeight,
        behavior: "smooth",
      });
    }
    
    // Close after short delay for better UX
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full h-11 px-3 py-2 flex items-center justify-between gap-2 rounded-md border bg-background text-sm transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          error ? "border-red-500 focus:ring-red-500" : "border-input",
          !value && "text-muted-foreground"
        )}
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>{value || placeholder}</span>
        </div>
        <svg
          className={cn(
            "h-4 w-4 transition-transform text-muted-foreground",
            isOpen && "rotate-180"
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Wheel Picker */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 w-full bg-background border rounded-xl shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
          style={{ maxHeight: "280px" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b bg-muted/50">
            <p className="text-sm font-medium text-center text-muted-foreground">
              Selecciona la hora de recogida
            </p>
          </div>

          {/* Wheel Container */}
          <div className="relative">
            {/* Selection highlight band */}
            <div 
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none z-10"
              style={{ height: itemHeight }}
            >
              <div className="h-full mx-2 bg-primary/10 rounded-lg border-2 border-primary/30" />
            </div>

            {/* Gradient overlays for depth effect */}
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-background to-transparent pointer-events-none z-20" />
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none z-20" />

            {/* Scrollable time slots */}
            <div
              ref={scrollRef}
              className="overflow-y-auto scrollbar-hide"
              style={{
                height: itemHeight * 5, // Show 5 items
                scrollSnapType: "y mandatory",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {/* Top padding for centering */}
              <div style={{ height: itemHeight * 2 }} />
              
              {timeSlots.map((slot, index) => {
                const isSelected = slot === value;
                const distance = currentIndex >= 0 ? Math.abs(index - currentIndex) : 0;
                
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleItemClick(slot, index)}
                    className={cn(
                      "w-full flex items-center justify-center transition-all duration-150",
                      "hover:bg-accent/50",
                      isSelected && "text-primary font-bold",
                      !isSelected && distance === 1 && "text-foreground/70",
                      !isSelected && distance >= 2 && "text-muted-foreground/50"
                    )}
                    style={{
                      height: itemHeight,
                      scrollSnapAlign: "center",
                      fontSize: isSelected ? "1.25rem" : distance <= 1 ? "1rem" : "0.875rem",
                      transform: `scale(${isSelected ? 1.1 : distance <= 1 ? 1 : 0.9})`,
                    }}
                  >
                    {slot}
                  </button>
                );
              })}
              
              {/* Bottom padding for centering */}
              <div style={{ height: itemHeight * 2 }} />
            </div>
          </div>

          {/* Footer with confirm button */}
          <div className="px-4 py-3 border-t bg-muted/50">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Confirmar {value || ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
